import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Theme, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Document } from 'react-pdf';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { SizeMe } from 'react-sizeme';
import classNames from 'classnames';
import { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';
import { cloneArray } from '../../../../../lib/Array';
import { FullScreenTrackingContext } from '../../../../Rooms/Tracking/FullScreenTrackingContext';
import { isWindows } from '../../../../../lib/Device';
import { PDFViewerProps } from '../PDFViewerProps';
import DocumentViewerActions from '../../../Actions/DocumentViewerActions';
import PDFError from '../../PDFError';
import pdfjs from '../../../../../config/pdfjs';
import PDFLoader from '../../PDFLoader';
import PDFPage from './PDFPage';
import PDFViewerActions from '../../../Actions/PDFViewerActions';
import useArrowKeys from '../../../../../lib/hook/useArrowKeys';
import useDarkTheme from '../../../../../theme/styles/useDarkTheme';
import usePDFViewerStyles from '../../usePDFViewerStyles';
import { useI18n } from '../../../../../lib/hook/useI18n';
import { loadAsyncCss } from './helper';
import SnackbarStore from '../../../../../stores/observables/SnackbarStore';

const FIRST_PAGE = 1;

const useStyles = makeStyles((theme: Theme) => ({
  actions: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    paddingTop: theme.spacing(1),
  },
  content: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  fullScreen: {
    alignItems: 'center',
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
  },
  hide: {
    display: 'none',
  },
  viewer: {
    width: '100%',
  },
}));

const PDFjsViewer = React.forwardRef<HTMLDivElement, PDFViewerProps>(
  (props: PDFViewerProps, ref) => {
    const { setFullScreenContentId } = useContext(FullScreenTrackingContext);

    const { t } = useI18n();
    const classes = useStyles();
    const pdfViewerClasses = usePDFViewerStyles();
    const darkThemeClasses = useDarkTheme();
    const fullScreenHandle = useFullScreenHandle();

    const {
      contentId,
      cover,
      darkTheme = false,
      document,
      downloadable = false,
      downloadTrigger,
      enableArrows = false,
      enableViewportDetection = false,
      initialPage,
      onDownload,
      onLoad,
      onLoadError,
      onPageChange,
      previewDialog = false,
      rounded = false,
      trackingProperties,
    } = props;

    const { height, mime, pdfUrl, width } = document;

    const [docHeight, setDocHeight] = useState<number>();
    const [docPages, setDocPages] = useState(0);
    const [docWidth, setDocWidth] = useState<number>(300);
    const [initialPageReady, setInitialPageReady] = useState(false);
    const [pageNumber, setPageNumber] = useState(initialPage);
    const [pdf, setPDF] = useState<PDFDocumentProxy>();
    // Stored rendered pages to control DOM nodes
    const [renderedPages, setRenderedPages] = useState<number[]>([]);
    const [tryPDF, setTryPDF] = useState(true);

    const { current: isWindowsOS } = useRef(isWindows());

    // load async the css for pdfjs
    useEffect(() => {
      loadAsyncCss(
        '//cdnjs.cloudflare.com/ajax/libs/pdf.js/2.12.313/pdf_viewer.css'
      );
    }, []);

    /**
     * @warning #184047520 - This is a dirty hack to attempt to fix PDF viewer
     * issues with files that were converted from .pptx or other formats to
     * PDF and had issues with text rendering. It's not entirely known
     * how or why this works, but it works, so make sure to check twice before
     * removing or editing this hook.
     *
     * @see https://www.geeksforgeeks.org/how-to-sharpen-blurry-text-in-html5-canvas/
     * @see https://www.bennadel.com/blog/4311-rendering-wrapped-text-to-a-canvas-in-javascript.htm
     */
    useEffect(() => {
      const initialDevicePixelRatio = window.devicePixelRatio;

      // @ts-ignore
      window.devicePixelRatio = 2;

      return () => {
        // @ts-ignore
        window.devicePixelRatio = initialDevicePixelRatio;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Every document is displayed using the described flow:
     * 1. Load the PDF
     * 2. When the PDF is loaded, we set the pdfjs instance and page count
     * 3. When the pdfjs instance is set, then we bootstrap the first page
     * 4. For each page, detect the dimensions and orientation to render it inside the available space
     */
    useEffect(() => {
      if (!pdf) return;
      goToPage(initialPage ?? FIRST_PAGE);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pdf]);

    /**
     * Control rendered pages
     * 1. When the document is loaded, we pre-render the first, last, initial
     *    page, previous and next pages
     * 2. Everytime the page cursor changes, the previous and next pages
     *    are added into the array to control the rendered pages
     * @note use stateful array instead of ref to prevent rendering issues
     */
    useEffect(() => {
      if (!pdf || !pageNumber) return;

      // Initialize rendered page collector
      const newRenderedPages = cloneArray(renderedPages);
      if (newRenderedPages.length === 0) {
        // Always render first and last page
        newRenderedPages.push(FIRST_PAGE);
        // For single-page docs, avoid duplicating the first page
        if (pdf.numPages > 1) {
          newRenderedPages.push(pdf.numPages);
        }
      }

      // Render previous, current and next page
      [pageNumber - 1, pageNumber, pageNumber + 1].forEach((page) => {
        if (
          // Prevent rendering a page twice
          !newRenderedPages.find((i) => i === page) &&
          page > 0 &&
          page <= pdf.numPages
        ) {
          newRenderedPages.push(page);
        }
      });

      setRenderedPages(newRenderedPages);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pdf, pageNumber]);

    useEffect(() => {
      if (downloadable && downloadTrigger && onDownload) {
        onDownload();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [downloadable, downloadTrigger]);

    const loading = useMemo(
      () => tryPDF && !initialPageReady,
      [initialPageReady, tryPDF]
    );

    function goToPage(page: number) {
      if (!pdf) return;
      // Determine the page orientaton and size
      // This will also preload the first page data, then no loading screen is shown
      pdf.getPage(page).then(
        (pageProxy: PDFPageProxy) => {
          const viewport = pageProxy.getViewport();
          const { rotation, viewBox } = viewport;
          // Some documents are rotated by metadata
          // then the viewBox=[, , pW, pH] is inverted
          const isRotated = rotation % 180 !== 0;
          const pW = isRotated ? viewBox[3] : viewBox[2];
          const pH = isRotated ? viewBox[2] : viewBox[3];
          setDocHeight(pH);
          setDocWidth(pW);
          setPageNumber(page);
          if (onPageChange) onPageChange(page);
        },
        (e: any) => {
          SnackbarStore.show(`${e}`);
        }
      );
    }

    const firstPage = () => {
      if (loading || pageNumber === 1) return;
      goToPage(FIRST_PAGE);
    };

    const nextPage = () => {
      if (loading || pageNumber! >= docPages) return;
      const p = pageNumber! + 1;
      goToPage(p);
    };

    const prevPage = () => {
      if (loading || pageNumber! <= FIRST_PAGE) return;
      const p = pageNumber! - 1;
      goToPage(p);
    };

    const lastPage = () => {
      if (loading || pageNumber === docPages) return;
      goToPage(docPages);
    };

    // Arrow listeners can be toggle and always enable on full screen mode
    useArrowKeys(enableArrows || fullScreenHandle.active, {
      onDownKey: nextPage,
      onLeftKey: prevPage,
      onRightKey: nextPage,
      onUpKey: prevPage,
    });

    const handleLoadError = (error: Error) => {
      console.error(`PDFViewer onLoadError: ${error.message}`);

      setTryPDF(false);

      if (onLoadError) {
        onLoadError();
      }
    };

    const onLoadSuccess = (p: PDFDocumentProxy) => {
      setPDF(p);
      const { numPages } = p;
      setDocPages(numPages);
      if (onLoad) onLoad(numPages);
    };

    const onFullScreenChange = (isFullScreenActive: boolean) => {
      setFullScreenContentId(isFullScreenActive ? contentId : null);
    };

    const calculateScale = (size: any) => {
      // If there is any missing value for calculation, default to scale=1
      // Be aware sometimes `size.height` is not available
      if (!size.width || !docWidth || !docHeight) return 1;
      // On fullscreen mode
      if (fullScreenHandle.active) {
        // @see https://github.com/wojtekmaj/react-pdf/issues/129#issuecomment-359136222
        const windowDimensions =
          fullScreenHandle.node.current?.getBoundingClientRect();
        if (windowDimensions) {
          // Determine the maximum scale for the available height or width
          const scaledPageHeight =
            (windowDimensions.width * docHeight) / docWidth;
          const maxHeight = windowDimensions.height - 75;
          return scaledPageHeight <= maxHeight
            ? size.width / docWidth
            : maxHeight / docHeight;
        }
      }
      // Calculate canvas scale based on available width
      return size.width / docWidth;
    };

    /**
     * Two SizeMe are required in order to support
     *
     *   1. The document loader sizing (root)
     *   2. The document player sizing (internal)
     *
     * The internal SizeMe is important for fullscreen
     * and multi-orientation pages support
     */
    return (
      // The REF must be place in the ROOT node in order to let
      // the DocumentTracker + IntersectionObserver work
      <Box {...{ ref }}>
        <Box
          className={classNames(classes.viewer, {
            [pdfViewerClasses.viewerRounded]: rounded,
            [pdfViewerClasses.viewerWithBorder]: !darkTheme,
          })}
          p={darkTheme ? undefined : 1}
        >
          <FullScreen onChange={onFullScreenChange} handle={fullScreenHandle}>
            <SizeMe refreshMode="debounce">
              {({ size }) => (
                <Box className={classes.fullScreen}>
                  <Box className={classes.content}>
                    {tryPDF ? (
                      <>
                        <Box
                          className={classNames({
                            [pdfViewerClasses.previewDialog]:
                              previewDialog && !fullScreenHandle.active,
                          })}
                        >
                          <PDFLoader
                            cover={cover}
                            docHeight={height}
                            docWidth={width}
                            enableViewportDetection={enableViewportDetection}
                            ready={!loading}
                            width={size.width}
                          >
                            <Document
                              externalLinkTarget="_blank"
                              file={pdfUrl}
                              loading={<></>}
                              onLoadError={handleLoadError}
                              onLoadSuccess={onLoadSuccess}
                              options={{
                                disableAutoFetch: true,
                                disableStream: true,
                                /**
                                 * Disable font face to prevent text rendering issues in Windows
                                 * text looks garbled - distorted - cut off - with glyphs.
                                 *
                                 * @see https://github.com/mozilla/pdf.js/blob/cfac6fa511945ae5b75d3c27a9356527862a7bf6/src/display/api.js#L185
                                 */
                                disableFontFace: isWindowsOS,
                                /**
                                 * Load standard fonts on windows from pdfjs CDN, which uses the pdfjs version
                                 * from the package used by react-pdf. On windows, because of the
                                 * `disableFontFace=true`, there are certain fonts that aren't available
                                 * and cause a render issue, such as "FoxitSans.pfb". These fonts are
                                 * available in the /standard_fonts folder from pdfjs, so we use
                                 * this CDN to allow the viewer to download them.
                                 *
                                 * @see https://github.com/mozilla/pdf.js/tree/v2.12.313/external/standard_fonts
                                 *
                                 * Got this idea from:
                                 * @see https://github.com/wojtekmaj/react-pdf/tree/v5.7.2#setting-up-react-pdf-1
                                 * @see https://github.com/mozilla/pdf.js/blob/cc110b8542332ae263c82ddffd65f6f8f0a38758/src/core/standard_fonts.js#L94-L113
                                 */
                                standardFontDataUrl: isWindowsOS
                                  ? `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`
                                  : undefined,
                              }}
                            >
                              {renderedPages.map((page) => (
                                <PDFPage
                                  key={`page-${contentId}-${page}`}
                                  className={classNames({
                                    [classes.hide]: pageNumber !== page,
                                  })}
                                  onRenderSuccess={() => {
                                    if (page === (initialPage ?? FIRST_PAGE)) {
                                      setInitialPageReady(true);
                                    }
                                  }}
                                  pageNumber={page}
                                  scale={calculateScale(size)}
                                />
                              ))}
                            </Document>
                          </PDFLoader>
                        </Box>
                        <Box
                          className={classNames(classes.actions, {
                            [pdfViewerClasses.actionsDarkTheme]: darkTheme,
                          })}
                        >
                          <Box>
                            <PDFViewerActions
                              atFirstPage={pageNumber === FIRST_PAGE}
                              atLastPage={pageNumber === docPages}
                              darkTheme={darkTheme}
                              disabled={loading}
                              fullScreenHandle={fullScreenHandle}
                              onFirstPageClick={firstPage}
                              onLastPageClick={lastPage}
                              onNextPageClick={nextPage}
                              onPrevPageClick={prevPage}
                            />
                            <DocumentViewerActions
                              darkTheme={darkTheme}
                              disabled={loading}
                              downloadable={downloadable}
                              fullScreenHandle={fullScreenHandle}
                              onDownload={onDownload}
                              trackingProperties={trackingProperties}
                            />
                          </Box>
                          <Typography
                            color={loading ? 'textSecondary' : 'textPrimary'}
                            className={classNames({
                              [pdfViewerClasses.pageNumberFullScreen]:
                                fullScreenHandle.active,
                              [darkThemeClasses.primaryTextLight]: darkTheme,
                            })}
                            variant="caption"
                          >
                            {t('document.viewer.counter.pages', {
                              pageNumber: pageNumber ?? FIRST_PAGE,
                              allPages: docPages,
                            })}
                          </Typography>
                        </Box>
                      </>
                    ) : (
                      <PDFError
                        downloadable={downloadable}
                        onDownload={onDownload}
                        mime={mime}
                      />
                    )}
                  </Box>
                </Box>
              )}
            </SizeMe>
          </FullScreen>
        </Box>
      </Box>
    );
  }
);

export default PDFjsViewer;
